import {
    useEffect,
    useState,
    useMemo
} from "react";
import {
    Animated
} from "react-native";
import type {
    BadgeColorsHookConstantType,
    BadgeColorsHolder,
    BadgeLiveColors,
    BadgeColors
} from "./type";
import {
    useAnimatedState
} from "../../helpers/motion";

const getKey = (colors: BadgeColors) => [
    colors.containerColor,
    colors.borderColor,
    colors.titleColor,
    colors.iconColor
].join("|");

const useBadgeColors = ({
    currentColors,
    customTheme,
    transition
}: BadgeColorsHookConstantType) => {
    const key = getKey(currentColors);

    const [
        holder,
        setHolder
    ] = useState<BadgeColorsHolder>({
        from: currentColors,
        to: currentColors,
        count: 0,
        key
    });

    const [
        live,
        setLive
    ] = useState<BadgeLiveColors | null>(null);

    if(holder.key !== key) {
        setHolder({
            count: holder.count + 1,
            to: currentColors,
            from: holder.to,
            key
        });
    }

    const progress = useAnimatedState({
        onFinish: () => setLive(null),
        isNativeDriverEnabled: false,
        intent: "valueChange",
        value: holder.count,
        customTheme
    });

    const nodes = useMemo(() => {
        const getNode = (from: string, to: string) => progress.interpolate({
            extrapolate: "clamp",
            inputRange: [
                holder.count - 1,
                holder.count
            ],
            outputRange: [
                from,
                to
            ]
        });

        return {
            containerColor: getNode(holder.from.containerColor, holder.to.containerColor),
            borderColor: getNode(holder.from.borderColor, holder.to.borderColor),
            titleColor: getNode(holder.from.titleColor, holder.to.titleColor),
            iconColor: getNode(holder.from.iconColor, holder.to.iconColor)
        };
    }, [
        holder.count
    ]);

    useEffect(() => {
        if(transition === "none" || !holder.count) {
            return;
        }

        const titleId = nodes.titleColor.addListener(({
            value
        }) => {
            setLive((current) => ({
                iconColor: current?.iconColor ?? holder.from.iconColor,
                titleColor: String(value)
            }));
        });

        const iconId = nodes.iconColor.addListener(({
            value
        }) => {
            setLive((current) => ({
                titleColor: current?.titleColor ?? holder.from.titleColor,
                iconColor: String(value)
            }));
        });

        return () => {
            nodes.titleColor.removeListener(titleId);

            nodes.iconColor.removeListener(iconId);
        };
    }, [
        transition,
        nodes
    ]);

    if(transition === "none") {
        return {
            containerColor: currentColors.containerColor,
            borderColor: currentColors.borderColor,
            titleColor: currentColors.titleColor,
            iconColor: currentColors.iconColor
        };
    }

    return {
        containerColor: nodes.containerColor as Animated.AnimatedInterpolation<string> | string,
        borderColor: nodes.borderColor as Animated.AnimatedInterpolation<string> | string,
        titleColor: live?.titleColor ?? holder.to.titleColor,
        iconColor: live?.iconColor ?? holder.to.iconColor
    };
};
export default useBadgeColors;
